import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {EMPTY, Observable, Subject} from 'rxjs';
import {catchError, map, switchMap, takeUntil} from 'rxjs/operators';
import {PaymentService, PaymentStatusResponse, ExistingPaymentMethod} from '@app/services/api/payment.service';
import {ProductPackagesService} from '@app/services/api/product-packages.service';
import {ModulePackagesService} from '@app/services/api/module-packages.service';
import {ConfigAPIService} from '@app/services/api/config-api.service';
import {NotificationService} from '@app/services/notification.service';
import {PricingBreakdown, PricingPreviewResponse} from '@app/interfaces/checkout/pricing-breakdown';
import {ProductPackage} from '@app/interfaces/packages/product-package';
import {ModulePackage, ModulePackageBillingPeriod, STANDALONE_BILLED_MODULE_TYPES} from '@app/interfaces/packages/module-package';
import {ProductPackageCodeEnum} from '@app/enums/ProductPackageCodeEnum';
import {SidebarStateService} from '@app/services/util/sidebar-state.service';
import {ModulePackageActivationSyncService} from '@app/services/util/module-package-activation-sync.service';
import {AccountProductPackage} from '@app/interfaces/packages/account-product-package';

export type CheckoutType = 'product_package' | 'module_package';

export interface CheckoutDialogData {
  type: CheckoutType;
  // Product package data
  productPackage?: ProductPackage;
  productPackageType?: string; // 'monthly' | 'yearly'
  // Module package data
  modulePackage?: ModulePackage;
  /** CarPeak is billed per vehicle on an ongoing basis; other modules are not. */
  hasSubscription?: boolean;
  modulePackagePeriod?: ModulePackageBillingPeriod | null;
  ref?: string;
  nonDismissible?: boolean;
  backRouterLink?: string[];
}

export interface CheckoutDialogResult {
  success: boolean;
  activated?: boolean;
  accountPackageUuid?: string;
}

@Component({
  selector: 'app-checkout-dialog',
  templateUrl: './checkout-dialog.component.html',
  styleUrls: ['./checkout-dialog.component.scss'],
  standalone: false
})
export class CheckoutDialogComponent implements OnInit, OnDestroy {
  public isLoading = true;
  public isProcessing = false;
  public errorMessage: string | null = null;

  public pricingBreakdown: PricingBreakdown | null = null;
  public existingPaymentMethod: ExistingPaymentMethod | null = null;
  public useNewCard = false; // Flag to force new card authorization

  // Module-package billing period (only relevant when type === 'module_package' and both prices are set)
  public selectedPeriod: ModulePackageBillingPeriod | null = null;

  /** The account's own product package, loaded async in loadStripeConfig — drives
   *  whether the billing-period toggle should appear for a settled-but-unsigned
   *  MINI account's first module purchase. Undefined until the config call resolves. */
  private accountProductPackage?: AccountProductPackage;

  // Stripe modal state
  public showPaymentModal = false;
  public stripeClientSecret: string | null = null;
  public stripePublishableKey = '';
  public stripeDisabled = false; // Account bills manually via Odoo — hide all card UI

  private destroy$ = new Subject<void>();

  // Every pricing-preview request (initial load + period changes) is funneled through
  // this Subject and switchMap'd, so a newer request always cancels any still-pending
  // one — rapid period toggling (e.g. "Letno" then "Mesečno") can otherwise leave two
  // HTTP requests in flight where the LAST-ARRIVING response wins pricingBreakdown
  // regardless of which period the user actually last selected.
  private pricingPreviewRequest$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CheckoutDialogData,
    private dialogRef: MatDialogRef<CheckoutDialogComponent>,
    private paymentService: PaymentService,
    private productPackagesService: ProductPackagesService,
    private modulePackagesService: ModulePackagesService,
    private configApi: ConfigAPIService,
    private notificationService: NotificationService,
    private sidebarState: SidebarStateService,
    private activationSync: ModulePackageActivationSyncService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // switchMap cancels any still-pending pricing-preview request when a newer one
    // comes in, so a rapid double/triple period click can't let a stale response
    // overwrite pricingBreakdown after a newer request already resolved. The
    // requestedPeriod capture + the check in the subscribe below is a second,
    // defensive guard against any remaining race.
    this.pricingPreviewRequest$.pipe(
      switchMap(() => {
        const requestedPeriod = this.selectedPeriod;
        return this.buildPricingPreviewRequest().pipe(
          map((response) => ({requestedPeriod, response})),
          catchError((error) => {
            console.error('Error loading pricing preview:', error);
            this.errorMessage = error.error?.message || 'Failed to load pricing information';
            this.isLoading = false;
            return EMPTY;
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(({requestedPeriod, response}) => {
      // Stale-response guard: ignore a response for a period that is no longer
      // the current selection (should already be unreachable thanks to switchMap,
      // but kept as a belt-and-braces check).
      if (requestedPeriod !== this.selectedPeriod) return;

      if (response.success && response.pricing) {
        this.pricingBreakdown = response.pricing;
        if (response.existingPaymentMethod) {
          this.existingPaymentMethod = response.existingPaymentMethod;
        }
      } else {
        this.errorMessage = response.message || 'Failed to load pricing';
      }
      this.isLoading = false;
    });

    this.selectedPeriod = this.resolveInitialPeriod();
    // Consolidatable modules must price on the subscription's period, which is
    // only known once the account config loads. Defer their (single) preview to
    // loadStripeConfig so we don't fire a throwaway first call with the wrong
    // period. Everything else previews immediately.
    if (!this.isConsolidatableModule) {
      this.loadPricingPreview();
    }
    this.loadStripeConfig();

    // The backdrop is scoped to the content area (not the full viewport, see
    // .checkout-dialog-backdrop in styles.scss) so the sidebar stays usable
    // while this dialog is open. The sidebar's width isn't fixed — it toggles
    // between collapsed/expanded — and can still be toggled while this dialog
    // is open, so subscribe to shrunk$ instead of reading isShrunk once, or the
    // backdrop offset would go stale the moment the sidebar is toggled mid-dialog.
    this.sidebarState.shrunk$.pipe(takeUntil(this.destroy$)).subscribe((isShrunk) => {
      document.documentElement.style.setProperty(
        '--checkout-dialog-sidebar-width',
        isShrunk ? '64px' : '248px'
      );
    });
  }

  /**
   * Pick the initial billing period for a module package.
   * Priority: explicit data.modulePackagePeriod → available period → null (no toggle).
   */
  private resolveInitialPeriod(): ModulePackageBillingPeriod | null {
    if (this.data.type !== 'module_package' || !this.data.modulePackage) return null;
    if (this.data.modulePackagePeriod) return this.data.modulePackagePeriod;
    const periods = this.availablePeriods;
    if (!periods.length) return null;
    return periods.includes('monthly') ? 'monthly' : periods[0];
  }

  /**
   * Which billing periods the current module package offers (based on which prices are set).
   */
  get availablePeriods(): ModulePackageBillingPeriod[] {
    const mp = this.data.modulePackage;
    if (!mp) return [];
    const periods: ModulePackageBillingPeriod[] = [];
    if (mp.priceMonthly != null) periods.push('monthly');
    if (mp.priceYearly != null) periods.push('yearly');
    return periods;
  }

  /**
   * Consolidatable modules (everything except STANDALONE_BILLED_MODULE_TYPES)
   * bill as a line on the account's Autobrief subscription, so their period is
   * dictated by that subscription — the user does not pick it.
   */
  get isConsolidatableModule(): boolean {
    const type = this.data.modulePackage?.moduleType;
    return this.data.type === 'module_package' && !!type
      && !STANDALONE_BILLED_MODULE_TYPES.includes(type);
  }

  get showPeriodToggle(): boolean {
    return this.data.type === 'module_package'
      && this.availablePeriods.length > 1
      && (!this.isConsolidatableModule || this.isFirstModulePurchaseForUnsignedMini);
  }

  /** True when this purchase is what actually starts the account's own
   *  subscription: a settled MINI account (productCode === MINI) that hasn't
   *  signed anything yet. Every other case (already-paying tiers, or a MINI
   *  account that already bought a module before) is a plain upsell onto the
   *  existing cadence and gets no period choice. Mirrors
   *  ActivateModulePackageDialogComponent's hasBothPeriods gate — keep both
   *  in sync (BRIEFD-6162). */
  get isFirstModulePurchaseForUnsignedMini(): boolean {
    return this.accountProductPackage?.productPackage?.productCode === ProductPackageCodeEnum.SUBSCRIPTION_MINI
      && !this.accountProductPackage?.isSigned;
  }

  onPeriodChange(period: ModulePackageBillingPeriod): void {
    if (this.selectedPeriod === period || this.isProcessing) return;
    this.selectedPeriod = period;
    this.loadPricingPreview();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.documentElement.style.removeProperty('--checkout-dialog-sidebar-width');
  }

  /**
   * Load pricing preview from backend. Requests are funneled through
   * pricingPreviewRequest$ (see the switchMap wiring in ngOnInit) so a newer
   * call always supersedes a still-pending one.
   */
  public loadPricingPreview(): void {
    if (
      (this.data.type === 'product_package' && this.data.productPackage) ||
      (this.data.type === 'module_package' && this.data.modulePackage)
    ) {
      this.isLoading = true;
      this.errorMessage = null;
      this.pricingPreviewRequest$.next();
    } else {
      this.errorMessage = 'Invalid checkout data';
      this.isLoading = false;
    }
  }

  /**
   * Build the actual pricing-preview HTTP request for the current checkout type.
   * Reads this.selectedPeriod at SUBSCRIBE time (inside the switchMap callback in
   * ngOnInit), not at call time, so it always reflects whichever period triggered
   * this particular request.
   */
  private buildPricingPreviewRequest(): Observable<PricingPreviewResponse> {
    if (this.data.type === 'product_package' && this.data.productPackage) {
      return this.paymentService.previewProductPackagePricing(
        this.data.productPackage.uuid,
        this.data.productPackageType || 'monthly'
      );
    }
    return this.paymentService.previewModulePackagePricing(
      this.data.modulePackage!.uuid,
      this.selectedPeriod
    );
  }

  /**
   * Load Stripe configuration from account config
   */
  private loadStripeConfig(): void {
    this.configApi.getAccountConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (config) => {
          this.stripePublishableKey = config.stripePublishableKey || '';
          this.stripeDisabled = !!config.stripeDisabled;
          this.accountProductPackage = config.activeAccountProductPackage;

          // Consolidatable modules bill on the AB subscription's cycle. For upsells
          // (no explicit period given), force the period to match the subscription;
          // for first purchases (explicit period in data.modulePackagePeriod), honor
          // the user's choice without overwriting it. A settled-but-unsigned MINI
          // account has no subscription cycle to inherit, so it gets the toggle
          // instead (isFirstModulePurchaseForUnsignedMini) and keeps whatever
          // resolveInitialPeriod() already picked.
          if (this.isConsolidatableModule) {
            if (!this.data.modulePackagePeriod && !this.isFirstModulePurchaseForUnsignedMini) {
              this.selectedPeriod =
                String(config.activeAccountProductPackage?.subscriptionType) === 'yearly' ? 'yearly' : 'monthly';
            }
            this.loadPricingPreview();
          }
        },
        error: (error) => {
          console.error('Error loading config:', error);
          // Config failed: consolidatable modules deferred their preview to here,
          // so run it now (with the initial period) rather than leave the dialog
          // stuck loading.
          if (this.isConsolidatableModule) {
            this.loadPricingPreview();
          }
        }
      });
  }

  /**
   * Get package name for display
   */
  get packageName(): string {
    if (this.data.type === 'product_package' && this.data.productPackage) {
      return this.data.productPackage.name;
    }
    if (this.data.type === 'module_package' && this.data.modulePackage) {
      return this.data.modulePackage.name;
    }
    return '';
  }

  /**
   * Module description shown above the pricing breakdown so the user knows
   * what they're buying when arriving here from a locked-feature flow.
   */
  get packageDescription(): string {
    if (this.data.type === 'module_package' && this.data.modulePackage?.description) {
      return this.data.modulePackage.description;
    }
    return '';
  }

  /**
   * Get subscription type label
   */
  get subscriptionLabel(): string {
    if (this.data.type === 'product_package') {
      return this.data.productPackageType === 'yearly' ? $localize`Letna naročnina` : $localize`Mesečna naročnina`;
    }
    if (this.data.type === 'module_package') {
      if (this.selectedPeriod === 'yearly') return $localize`Letna naročnina`;
      if (this.selectedPeriod === 'monthly') return $localize`Mesečna naročnina`;
      return $localize`Mesečna naročnina`;
    }
    return $localize`Naročnina`;
  }

  /**
   * User wants to change their card - force new card authorization
   */
  onChangeCard(): void {
    this.useNewCard = true;
  }

  /**
   * User cancelled card change - use existing card
   */
  onCancelCardChange(): void {
    this.useNewCard = false;
  }

  /**
   * Handle confirm purchase button click
   */
  confirmPurchase(): void {
    if (this.isProcessing || !this.pricingBreakdown) return;

    this.isProcessing = true;
    this.errorMessage = null;

    // If user explicitly wants to change card, always show Stripe form
    // (even if finalAmount <= 0, we still need to authorize the new card)
    if (this.useNewCard) {
      this.initializePayment();
      return;
    }

    // Only MINI product package can skip card authorization entirely
    const isMiniProductPackage = this.data.type === 'product_package'
      && this.data.productPackage?.productCode === ProductPackageCodeEnum.SUBSCRIPTION_MINI;

    if (isMiniProductPackage && (this.pricingBreakdown.isDowngradeToFree || this.pricingBreakdown.finalAmount <= 0)) {
      this.activateDirectly();
      return;
    }

    // Initialize payment
    this.initializePayment();
  }

  /**
   * Activate package directly (for free packages or downgrades)
   */
  private activateDirectly(): void {
    if (this.data.type === 'product_package' && this.data.productPackage) {
      this.productPackagesService.changeProductPackage(
        this.data.productPackage.uuid,
        this.data.productPackageType || 'monthly'
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.handleSuccess();
          },
          error: (error) => {
            this.handleError(error);
          }
        });
    } else if (this.data.type === 'module_package' && this.data.modulePackage) {
      const hasSubscription = !!this.data.hasSubscription;
      this.modulePackagesService.activateModulePackage(
        this.data.modulePackage.uuid,
        hasSubscription,
        this.data.ref,
        this.selectedPeriod
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.handleSuccess();
          },
          error: (error) => {
            this.handleError(error);
          }
        });
    }
  }

  /**
   * Initialize payment flow
   */
  private initializePayment(): void {
    if (this.data.type === 'product_package' && this.data.productPackage) {
      this.paymentService.initializePayment({
        type: 'product_package',
        productPackageUuid: this.data.productPackage.uuid,
        productPackageType: this.data.productPackageType || 'monthly',
        forceNewCard: this.useNewCard
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.handlePaymentInitResponse(response);
          },
          error: (error) => {
            this.handleError(error);
          }
        });
    } else if (this.data.type === 'module_package' && this.data.modulePackage) {
      const hasSubscription = !!this.data.hasSubscription;
      this.paymentService.initializePayment({
        type: 'module_package',
        modulePackageUuid: this.data.modulePackage.uuid,
        hasSubscription,
        modulePackagePeriod: this.selectedPeriod,
        ref: this.data.ref,
        forceNewCard: this.useNewCard
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.handlePaymentInitResponse(response);
          },
          error: (error) => {
            this.handleError(error);
          }
        });
    }
  }

  /**
   * Handle payment initialization response
   */
  private handlePaymentInitResponse(response: any): void {
    // Already activated (existing payment method used, free package, or local dev)
    if (response.status === 'activated') {
      // Store existing payment method info for display if available
      if (response.existing_payment_method) {
        this.existingPaymentMethod = response.existing_payment_method;
      }
      this.handleSuccess();
      return;
    }

    // If authorization is required, show Stripe modal
    if (response.requires_authorization && response.stripe_client_secret) {
      this.stripeClientSecret = response.stripe_client_secret;
      this.showPaymentModal = true;
      this.isProcessing = false;
      return;
    }

    // Unexpected state
    this.isProcessing = false;
    this.errorMessage = $localize`Nepričakovano stanje. Prosimo, poskusite znova.`;
  }

  /**
   * Handle payment completion from Stripe modal
   */
  onPaymentComplete(response?: PaymentStatusResponse): void {
    this.showPaymentModal = false;
    this.stripeClientSecret = null;
    this.handleSuccess();
  }

  /**
   * Handle payment cancellation
   */
  onPaymentCancelled(): void {
    this.showPaymentModal = false;
    this.stripeClientSecret = null;
    this.isProcessing = false;
    this.notificationService.showInfoAlert($localize`Plačilo preklicano`);
  }

  /**
   * Handle payment failure
   */
  onPaymentFailed(errorMessage: string): void {
    this.showPaymentModal = false;
    this.stripeClientSecret = null;
    this.isProcessing = false;
    this.errorMessage = errorMessage || $localize`Avtorizacija plačila ni uspela. Prosimo, poskusite znova.`;
  }

  /**
   * Handle successful activation
   */
  private handleSuccess(): void {
    this.isProcessing = false;

    // Clear caches. Module packages additionally sync the account config
    // cache and notify VirtualSalonEntitlementRefreshService — this dialog
    // is opened for module-package purchases from several entry points
    // (marketplace, upsell, locked-feature clicks), and previously none of
    // them told the Virtual Salon shell's nav badge to re-check itself.
    if (this.data.type === 'product_package') {
      this.productPackagesService.clearCache();
    } else {
      this.activationSync.syncAfterChange();
    }

    const packageName = this.packageName;
    this.notificationService.showSuccessAlert(
      $localize`${packageName} uspešno aktiviran!`
    );

    this.dialogRef.close({
      success: true,
      activated: true
    } as CheckoutDialogResult);
  }

  /**
   * Handle error
   */
  private handleError(error: any): void {
    console.error('Checkout error:', error);
    this.isProcessing = false;
    this.errorMessage = error.error?.message || $localize`Prišlo je do napake. Prosimo, poskusite znova.`;
  }

  /**
   * Close dialog
   */
  cancel(): void {
    this.dialogRef.close({ success: false } as CheckoutDialogResult);
  }

  /**
   * Only relevant when data.nonDismissible is true (e.g. the Porocilo
   * non-PRO upsell) — navigates away instead of closing the dialog, since
   * disableClose:true on the MatDialog config already blocks backdrop/ESC.
   */
  goBack(): void {
    this.dialogRef.close({ success: false } as CheckoutDialogResult);
    if (this.data.backRouterLink) {
      this.router.navigate(this.data.backRouterLink);
    }
  }

  /**
   * Static method to open the dialog
   */
  static open(
    dialog: MatDialog,
    data: CheckoutDialogData
  ): MatDialogRef<CheckoutDialogComponent> {
    return dialog.open(CheckoutDialogComponent, {
      width: '480px',
      maxWidth: '95vw',
      disableClose: !!data.nonDismissible,
      backdropClass: 'checkout-dialog-backdrop',
      data
    });
  }
}
